import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../../services/api';
import { getSubdomainUsername, getUsernameSyncOnly } from '../../../utils/subdomain';
import { buildMediaUrl } from '../../../utils/mediaUrl';

const PublicHowItWorks = () => {
  const { username: urlUsername } = useParams();
  const subdomainUser = getSubdomainUsername();
  const cachedCustomDomainUser = getUsernameSyncOnly();
  const username = urlUsername || subdomainUser || cachedCustomDomainUser;

  const [data, setData] = useState({ section: null, steps: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/business/how-it-works/public/${username}/`);
        setData({
          section: res.data.section || {},
          steps: res.data.steps || []
        });
      } catch (err) {
        console.error('Error fetching how it works:', err);
      } finally {
        setLoading(false);
      }
    };
    if (username) fetchData();
  }, [username]);

  if (loading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{
          width: '2rem', height: '2rem',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }} />
      </div>
    );
  }

  if (!data.steps.length) {
    return (
      <div id="section-how_it_works" style={{ padding: '4rem 2rem', textAlign: 'center', backgroundColor: '#1e293b' }}>
        <p style={{ color: 'rgba(255,255,255,0.5)' }}>No process steps available yet.</p>
      </div>
    );
  }

  const layoutVariant = data.section?.layout_variant || 'cards';
  const bgColor = data.section?.background_color || '#1e293b';

  return (
    <div style={{ padding: '4rem 2rem', backgroundColor: bgColor }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
            {data.section?.title || 'How It Works'}
          </h2>
          {data.section?.subtitle && (
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>
              {data.section.subtitle}
            </p>
          )}
        </div>

        {/* Cards Layout */}
        {layoutVariant === 'cards' && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '2rem'
          }}>
            {data.steps.map((step, index) => (
              <div
                key={step.id || index}
                style={{
                  padding: '2rem',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '1rem',
                  textAlign: 'center',
                  position: 'relative'
                }}
              >
                <div style={{
                  position: 'absolute',
                  top: '-1rem',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '2.5rem',
                  height: '2.5rem',
                  backgroundColor: '#6366f1',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: '700',
                  fontSize: '1rem'
                }}>
                  {index + 1}
                </div>
                {step.image && (
                  <img
                    src={buildMediaUrl(step.image)}
                    alt={step.title}
                    style={{
                      width: '80px',
                      height: '80px',
                      objectFit: 'cover',
                      borderRadius: '0.75rem',
                      margin: '1rem auto'
                    }}
                  />
                )}
                <h3 style={{ color: '#fff', fontSize: '1.25rem', fontWeight: '600', marginTop: '1rem', marginBottom: '0.5rem' }}>
                  {step.title}
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6' }}>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* Timeline Layout */}
        {layoutVariant === 'timeline' && (
          <div style={{ position: 'relative', maxWidth: '600px', margin: '0 auto' }}>
            <div style={{
              position: 'absolute',
              left: '1.25rem',
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: 'rgba(99, 102, 241, 0.3)'
            }} />
            {data.steps.map((step, index) => (
              <div
                key={step.id || index}
                style={{
                  display: 'flex',
                  gap: '1.5rem',
                  marginBottom: '2rem',
                  position: 'relative'
                }}
              >
                <div style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  backgroundColor: '#6366f1',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontWeight: '700',
                  flexShrink: 0,
                  zIndex: 1
                }}>
                  {index + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                    {step.title}
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.6' }}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Numbered Layout */}
        {layoutVariant === 'numbered' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '800px', margin: '0 auto' }}>
            {data.steps.map((step, index) => (
              <div
                key={step.id || index}
                style={{
                  display: 'flex',
                  gap: '1.5rem',
                  padding: '1.5rem',
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderRadius: '1rem',
                  alignItems: 'center'
                }}
              >
                <div style={{
                  fontSize: '2.5rem',
                  fontWeight: '700',
                  color: '#6366f1',
                  minWidth: '3rem'
                }}>
                  {String(index + 1).padStart(2, '0')}
                </div>
                <div>
                  <h3 style={{ color: '#fff', fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                    {step.title}
                  </h3>
                  <p style={{ color: 'rgba(255,255,255,0.6)', lineHeight: '1.5', fontSize: '0.95rem' }}>
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicHowItWorks;
